import React from "react";
import { Check, Minus } from "lucide-react";

export interface TriStateCheckboxProps {
	/**
	 * The current state of the checkbox
	 * - false: unchecked
	 * - true: fully checked
	 * - "indeterminate": partially checked
	 */
	checked: boolean | "indeterminate";

	/**
	 * Callback fired when the checkbox state changes
	 */
	onChange?: (checked: boolean | "indeterminate") => void;

	/**
	 * Whether the checkbox is disabled
	 */
	disabled?: boolean;

	/**
	 * Additional CSS classes
	 */
	className?: string;

	/**
	 * Size variant
	 */
	size?: "sm" | "md" | "lg";

	/**
	 * Custom label for accessibility
	 */
	"aria-label"?: string;

	/**
	 * If true, stop event propagation on click and keyboard events
	 */
	stopPropagation?: boolean;
}

export const TriStateCheckbox: React.FC<TriStateCheckboxProps> = ({
	checked,
	onChange,
	disabled = false,
	className = "",
	size = "md",
	"aria-label": ariaLabel,
	stopPropagation = false,
}) => {
	const handleClick = () => {
		if (disabled || !onChange) return;

		// Cycle through states: unchecked -> checked -> unchecked
		// (indeterminate is typically set programmatically, not by user clicks)
		if (checked === false) {
			onChange(true);
		} else {
			onChange(false);
		}
	};

	const sizeClasses = {
		sm: "h-4 w-4",
		md: "h-5 w-5",
		lg: "h-6 w-6",
	};

	const iconSizeClasses = {
		sm: "h-3 w-3",
		md: "h-3.5 w-3.5",
		lg: "h-4 w-4",
	};

	const getCheckboxStyles = () => {
		const baseStyles = `
			inline-flex items-center justify-center rounded border-2 cursor-pointer transition-all duration-200
			focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
			${sizeClasses[size]}
		`;

		if (disabled) {
			return `${baseStyles} bg-gray-100 border-gray-300 cursor-not-allowed opacity-50`;
		}

		switch (checked) {
			case true:
				return `${baseStyles} bg-blue-600 border-blue-600 text-white hover:bg-blue-700 hover:border-blue-700`;
			case "indeterminate":
				return `${baseStyles} bg-blue-600 border-blue-600 text-white hover:bg-blue-700 hover:border-blue-700`;
			case false:
			default:
				return `${baseStyles} bg-white border-gray-300 hover:border-gray-400 hover:bg-gray-50`;
		}
	};

	const renderIcon = () => {
		if (checked === true) {
			return <Check className={iconSizeClasses[size]} strokeWidth={2.5} />;
		}
		if (checked === "indeterminate") {
			return <Minus className={iconSizeClasses[size]} strokeWidth={2.5} />;
		}
		return null;
	};

	return (
		<div
			className={`${getCheckboxStyles()} ${className}`}
			onClick={(e) => {
				if (stopPropagation) e.stopPropagation();
				handleClick();
			}}
			role="checkbox"
			aria-checked={checked === "indeterminate" ? "mixed" : checked}
			aria-label={ariaLabel}
			tabIndex={disabled ? -1 : 0}
			onKeyDown={(e) => {
				if (stopPropagation) e.stopPropagation();
				if (e.key === " " || e.key === "Enter") {
					e.preventDefault();
					handleClick();
				}
			}}
		>
			{renderIcon()}
		</div>
	);
};

export default TriStateCheckbox;
